import React, { useState } from 'react';
import { storage, db, auth } from '../../firebase';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { addDoc, collection, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { useTranslatedText } from '../i18n/useTranslatedText.js';
import './ResourceCreation.css';

const ResourceCreation = () => {
  const [thumbnail, setThumbnail] = useState(null);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [publishDate, setPublishDate] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const pageTitle = useTranslatedText('İçerik Oluştur');
  const thumbnailLabel = useTranslatedText('Başlık Resmi:');
  const titleLabel = useTranslatedText('Başlık:');
  const authorLabel = useTranslatedText('Oluşturan:');
  const publishDateLabel = useTranslatedText('Yayınlama Tarihi:');
  const descriptionLabel = useTranslatedText('Açıklama:');
  const filesLabel = useTranslatedText('Dosyalar (PDF, JPG, PNG, MP3, MP4):');
  const uploadingText = useTranslatedText('Yükleniyor...');
  const uploadButtonText = useTranslatedText('Kaynak Yükle');
  const successText = useTranslatedText('Başarıyla yüklendi!');

  const handleThumbnailChange = (e) => {
    if (e.target.files[0]) {
      setThumbnail(e.target.files[0]);
    }
  };

  const handleFilesChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    setSuccessMessage('');
    try {

      const user = auth.currentUser;
            if (!user) {
              return;
            }

      // Create the Firestore doc first so Storage rules can enforce ownership by resourceId.
      const resourceDocRef = await addDoc(collection(db, 'resources'), {
        thumbnailUrl: '',
        title,
        author,
        publishDate,
        description,
        files: [],
        status: 'pending',
        createdBy: user.uid,
      });

      const resourceId = resourceDocRef.id;
      const sanitizedTitle = title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const resourceDirectory = `resources/${resourceId}_${sanitizedTitle}`;

      const uploadedRefs = [];
      try {
        // Upload thumbnail
          let thumbnailUrl = '';
          if (thumbnail) {
            const uniqueThumbName = `thumb_${uuidv4()}_${thumbnail.name}`;
            const thumbRef = ref(storage, `${resourceDirectory}/${uniqueThumbName}`);
            await uploadBytes(thumbRef, thumbnail, {
              contentType: thumbnail.type || 'application/octet-stream',
            });
            uploadedRefs.push(thumbRef);
            thumbnailUrl = await getDownloadURL(thumbRef);
          }

        // Upload resource files and store their paths
        const filePaths = [];
        for (let file of files) {
          const uniqueFileName = `${uuidv4()}_${file.name}`;
          const fileRef = ref(storage, `${resourceDirectory}/${uniqueFileName}`);
          await uploadBytes(fileRef, file, {
            contentType: file.type || 'application/octet-stream',
          });
          uploadedRefs.push(fileRef);
          filePaths.push(fileRef.fullPath);
        }

        await updateDoc(doc(db, 'resources', resourceId), {
          thumbnailUrl,
          files: filePaths,
        });
      } catch (error) {
        // Best-effort cleanup: remove any uploaded files and the stub doc.
        console.error('Error uploading resource files:', error);
        await Promise.all(
          uploadedRefs.map(async (storageRef) => {
            try {
              await deleteObject(storageRef);
            } catch (deleteError) {
              console.error('Error cleaning up uploaded file:', deleteError);
            }
          })
        );
        await deleteDoc(doc(db, 'resources', resourceId));
        throw error;
      }

      setSuccessMessage(successText);
      // Optionally clear form fields here...
    } catch (error) {
      console.error("Error creating resource:", error);
      // Optionally set an error message
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="resource-creation-form">
      <h2>{pageTitle}</h2>
      <form onSubmit={handleSubmit}>
        <label>{thumbnailLabel}</label>
        <input type="file" accept="image/*" onChange={handleThumbnailChange} required />

        <label>{titleLabel}</label>
        <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />

        <label>{authorLabel}</label>
        <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)} required />

        <label>{publishDateLabel}</label>
        <input type="text" value={publishDate} onChange={(e) => setPublishDate(e.target.value)} required />

        <label>{descriptionLabel}</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} required />

        <label>{filesLabel}</label>
        <input type="file" accept=".pdf, image/*, audio/*, video/*" multiple onChange={handleFilesChange} required />

        <button type="submit" disabled={uploading} className="submit">
          {uploading ? uploadingText : uploadButtonText}
        </button>

        {successMessage && <div className="success-message">{successMessage}</div>}
      </form>
    </div>
  );
};

export default ResourceCreation;
